import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Users,
  Activity,
  TrendingUp,
  BookOpen,
  Target,
  Clock,
  BarChart3,
  PieChart,
  Calendar,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Minus,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];

const PERIOD_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "60", label: "Last 60 days" },
  { value: "90", label: "Last 90 days" },
];

const AnalyticsDashboard = () => {
  const { token, isSuperAdmin, isOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("30");
  const [refreshing, setRefreshing] = useState(false);

  const canViewAnalytics = isSuperAdmin || isOwner;

  useEffect(() => {
    if (canViewAnalytics) {
      fetchAnalytics();
    }
  }, [canViewAnalytics, period]);

  const fetchAnalytics = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/analytics/dashboard?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num?.toString() || "0";
  };

  const formatPercent = (num) => {
    return (num || 0).toFixed(1) + "%";
  };

  if (!canViewAnalytics) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-8 text-center">
          <BarChart3 className="w-12 h-12 text-amber-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-amber-900 mb-2">
            Analytics Access Required
          </h3>
          <p className="text-amber-700">
            Only Super Admins and Owners can view analytics.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <BarChart3 className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Analytics Dashboard</h2>
            <p className="text-sm text-slate-600">
              Platform insights and performance metrics
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchAnalytics} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Users"
          value={formatNumber(data?.users?.total)}
          icon={Users}
          color="blue"
          subtext={`+${data?.users?.new_week || 0} this week`}
        />
        <MetricCard
          title="Daily Active"
          value={formatNumber(data?.users?.dau)}
          icon={Activity}
          color="green"
          subtext={`${formatPercent(data?.users?.dau / data?.users?.total * 100)} of total`}
        />
        <MetricCard
          title="Total Attempts"
          value={formatNumber(data?.learning?.total_attempts)}
          icon={BookOpen}
          color="purple"
          subtext={`${data?.learning?.attempts_today || 0} today`}
        />
        <MetricCard
          title="Avg Accuracy"
          value={formatPercent(data?.learning?.overall_accuracy)}
          icon={Target}
          color="amber"
          subtext={`${Math.round(data?.learning?.avg_time_per_passage || 0)}s avg time`}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              User Registrations
            </CardTitle>
            <CardDescription>New user signups over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.users?.registration_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => v?.slice(5) || ""} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#10b981" fill="#10b98133" name="New Users" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Learning Activity Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              Learning Activity
            </CardTitle>
            <CardDescription>Daily attempts and accuracy trend</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data?.learning?.attempts_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => v?.slice(5) || ""} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="attempts" stroke="#8b5cf6" name="Attempts" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="accuracy" stroke="#f59e0b" name="Accuracy %" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Category Performance</CardTitle>
            <CardDescription>Attempts by category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.learning?.category_breakdown || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="category" type="category" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="attempts" fill="#3b82f6" name="Attempts" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Difficulty Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Difficulty Distribution</CardTitle>
            <CardDescription>Attempts by difficulty level</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.learning?.difficulty_breakdown || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="difficulty" tick={{ fontSize: 12 }} tickFormatter={(v) => `Level ${v}`} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="attempts" fill="#8b5cf6" name="Attempts" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="accuracy" fill="#10b981" name="Accuracy %" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Traffic Sources */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Traffic Sources</CardTitle>
            <CardDescription>Where visitors come from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {data?.traffic?.sources?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={data.traffic.sources}
                      dataKey="count"
                      nameKey="source"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ source, percent }) => `${source} ${(percent * 100).toFixed(0)}%`}
                    >
                      {data.traffic.sources.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  <p>No traffic data yet. Events will appear as users visit.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Weekly Active"
          value={data?.users?.wau || 0}
          total={data?.users?.total || 1}
          icon={Activity}
        />
        <StatCard
          title="Monthly Active"
          value={data?.users?.mau || 0}
          total={data?.users?.total || 1}
          icon={Calendar}
        />
        <StatCard
          title="7-Day Retention"
          value={data?.growth?.retention_7d_rate || 0}
          isPercent
          icon={TrendingUp}
        />
        <StatCard
          title="Inactive Users"
          value={data?.growth?.inactive_users || 0}
          total={data?.users?.total || 1}
          icon={Users}
          negative
        />
      </div>

      {/* Engagement Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Engagement Overview</CardTitle>
          <CardDescription>User engagement breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <EngagementItem
              label="Guest Events"
              value={data?.users?.guest_events || 0}
              subtext={`${formatPercent(data?.users?.guest_ratio)} of total`}
              icon={Globe}
            />
            <EngagementItem
              label="Logged-in Events"
              value={data?.users?.logged_in_events || 0}
              subtext={`${formatPercent(100 - (data?.users?.guest_ratio || 0))} of total`}
              icon={Users}
            />
            <EngagementItem
              label="New Users (Period)"
              value={data?.users?.new_period || 0}
              subtext={`${data?.users?.new_today || 0} today`}
              icon={TrendingUp}
            />
            <EngagementItem
              label="Avg Time/Passage"
              value={`${Math.round(data?.learning?.avg_time_per_passage || 0)}s`}
              subtext="seconds average"
              icon={Clock}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper Components
const MetricCard = ({ title, value, icon: Icon, color, subtext }) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    amber: "bg-amber-100 text-amber-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-600">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
          </div>
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatCard = ({ title, value, total, icon: Icon, isPercent, negative }) => {
  const percentage = isPercent ? value : (value / total * 100);
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${negative ? "bg-rose-100" : "bg-slate-100"}`}>
            <Icon className={`w-4 h-4 ${negative ? "text-rose-600" : "text-slate-600"}`} />
          </div>
          <div>
            <p className="text-sm text-slate-600">{title}</p>
            <p className="text-lg font-semibold">
              {isPercent ? `${value.toFixed(1)}%` : formatNumber(value)}
            </p>
            {!isPercent && (
              <p className="text-xs text-slate-500">{percentage.toFixed(1)}% of total</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EngagementItem = ({ label, value, subtext, icon: Icon }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-slate-100 rounded-lg">
      <Icon className="w-4 h-4 text-slate-600" />
    </div>
    <div>
      <p className="text-sm text-slate-600">{label}</p>
      <p className="text-lg font-semibold text-slate-900">{typeof value === 'number' ? formatNumber(value) : value}</p>
      {subtext && <p className="text-xs text-slate-500">{subtext}</p>}
    </div>
  </div>
);

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num?.toString() || "0";
};

export default AnalyticsDashboard;
